import { BackButton, BREAKPOINTS, TextButton as Button, COLORS, ComponentL, ComponentTextStyle, DropdownFilter, LoadingIndicator, Size } from "@laerdal/life-react-components";
import axios from "axios";
import styled from "styled-components";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import UrgentAssistance from "../../commonComponents/UrgentAssistance";
import { PageWidthFull } from "../../commonComponents/PageWidthFull";
import ProductApi from "../../services/api/ProductApi";
import { ProductLineDetailsDto } from "../../model/dto/productLineDetailsDto";
import { ProductRelevantArticlesDto } from "../../model/dto/productRelevantArticlesDto";
import { buildMarketPath, selectMarket, selectTranslateWithAi } from "../../store/slices/globalConfigSlice";
import { marketList } from "../../model/constants/marketContants";
import { useMediaMatch } from "rooks";
import VerticalTab from "../../commonComponents/VerticalTab";
import posthog from "posthog-js";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 32px 0 56px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h3`
  color: ${COLORS.neutral_800};
  font-family: Lato;
  font-size: 32px;
  font-weight: 700;
  line-height: 120%;
  margin: 0;

  ${BREAKPOINTS.MEDIUM} {
    font-size: 40px;
  }
`;

const Subtitle = styled(ComponentL)`
  color: ${COLORS.neutral_600};
  font-size: 20px;
`;

const Components = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const VerticalTabsContainer = styled.div`
  display: flex;
  width: 280px;
  flex-direction: column;
`;

const Articles = styled.div`
  border-radius: 8px;
  border: 1px solid ${COLORS.neutral_200};
  background: ${COLORS.white};
  padding: 32px;

  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const LoadingIndicatorContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: center;
`;

const FaqLink = styled.a`
  &:link, &:visited, &:hover, &:active {
    color: ${COLORS.primary_600};
    text-decoration: none;
  }

  color: ${COLORS.primary_600};
  font-size: 17px;
  font-style: normal;
  font-weight: 400;
  line-height: 25px;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${COLORS.neutral_20};
  border-radius: 8px;
  padding: 16px;
  margin-top: 24px;

  ${BREAKPOINTS.MEDIUM} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
  }
`;

const FooterText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FooterTitle = styled.div`
  font-family: Lato;
  font-size: 24px;
  font-weight: 700;
  color: ${COLORS.neutral_800};
`;

const FooterSubtitle = styled.div`
  font-family: Lato;
  font-size: 16px;
  font-weight: 400;
  color: ${COLORS.neutral_600};
`;

const ContactButton = styled(Button)`
  width: 100%;
  height: 56px;
  font-size: 20px;
  font-weight: 400;
  ${BREAKPOINTS.MEDIUM} {
    width: auto;
  }
`;

const UrgentAssistanceContainer = styled.div`
  margin-bottom: 20px;
`;

const parseProductRoute = (value?: string | null) => {
  if (!value) return null;
  const match = value.match(/\/category\/([^/]+)\/product\/([^/?#]+)/i);
  if (!match) return null;
  return { categoryLink: match[1], productLink: match[2] };
};

const CaseAssetInfoPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation("cases");
  const currentMarket = useSelector(selectMarket);
  const translateWithAi = useSelector(selectTranslateWithAi);
  const isMediumScreen = useMediaMatch(BREAKPOINTS.MEDIUM.replace('@media ', ''));

  const productName = searchParams.get("productName") || "";
  const productLineName = searchParams.get("productLineName") || "";
  const productLineCode = searchParams.get("productLineCode") || "";
  const productSerial = searchParams.get("productSerial") || "";
  const suboption = searchParams.get("suboption") || "technicalSupport";
  const categoryLink = searchParams.get("categoryLink");
  const redirectUrl = searchParams.get("redirectUrl");

  const routeFromRedirect = parseProductRoute(redirectUrl);
  const [resolvedCategoryLink, setResolvedCategoryLink] = useState<string>(
    categoryLink || routeFromRedirect?.categoryLink || ""
  );
  const [resolvedProductLink, setResolvedProductLink] = useState<string>(
    productLineCode || routeFromRedirect?.productLink || ""
  );

  const [productLineLoading, setProductLineLoading] = useState<boolean>(true);
  const [productLine, setProductLine] = useState<ProductLineDetailsDto | undefined>(undefined);
  const [componentLinkState, setComponentLinkState] = useState<string>("");
  const [articlesLoading, setArticlesLoading] = useState<boolean>(true);
  const [articlesDto, setArticlesDto] = useState<ProductRelevantArticlesDto | undefined>(undefined);

  const canLoadContent = !!resolvedProductLink;

  useEffect(() => {
    if (resolvedProductLink) {
      return;
    }

    let isActive = true;

    ProductApi
      .GetProductLineCodeByAgileCode(productLineName)
      .then((code) => {
        if (!isActive || !code) 
          return;
        setResolvedProductLink(code);
      })
      .catch(() => {
        navigate(buildCaseFormUrl(), { replace: true });
      });

    return () => {
      isActive = false;
    };
  }, [resolvedProductLink, productLineName]);

  useEffect(() => {
    if (!canLoadContent) {
      setProductLine(undefined);
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;
    setProductLineLoading(true);

    console.log(resolvedCategoryLink, resolvedProductLink, translateWithAi, currentMarket, canLoadContent);
    const lang = "en";//(resolvedCategoryLink != 'medical-devices' && translateWithAi) ? marketList[currentMarket].locale : undefined;

    ProductApi
      .GetProductLine(resolvedProductLink, lang, signal)
      .then((data) => {
        // If no data or no components, redirect to the case create form
        if (!data || !data.components || data.components.length === 0) {
          navigate(buildCaseFormUrl(), { replace: true });
          return;
        }
        setProductLine(data);
        setComponentLinkState(data.components[0].link);
      })
      .catch((error) => {
        if (axios.isCancel(error)) 
          return;
        navigate(buildCaseFormUrl(), { replace: true });
      })
      .finally(() => {
        setProductLineLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [resolvedCategoryLink, resolvedProductLink, translateWithAi, currentMarket, canLoadContent]);

  useEffect(() => {
    if (!componentLinkState || !productLine || !canLoadContent) {
      setArticlesDto(undefined);
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;
    setArticlesLoading(true);

    ProductApi
      .GetRelevantArticlesLine(
        resolvedCategoryLink,
        resolvedProductLink,
        componentLinkState,
        (resolvedCategoryLink != 'medical-devices' && translateWithAi) ? marketList[currentMarket].locale : undefined,
        signal
      )
      .then((data) => {
        setArticlesDto(data);
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
      })
      .finally(() => {
        setArticlesLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [productLine, componentLinkState, resolvedCategoryLink, resolvedProductLink, translateWithAi, currentMarket, canLoadContent]);

  const componentItems = useMemo(() => {
    if (!productLine) return [];
    return productLine.components.slice(0, 6).map((component) => ({
      name: component.name,
      link: component.link,
    }));
  }, [productLine]);

  const articleImpressionKeyRef = useRef<string>("");
  const productLineForEvents = productLineCode || resolvedProductLink || productName;

  const articleImpressions = useMemo(() => {
    return (articlesDto?.articles || []).map((item) => ({
      title: item.name,
      number: item.articleNumber
    }));
  }, [articlesDto?.articles]);

  useEffect(() => {
    if (articleImpressions.length === 0) return;
    const key = `${componentLinkState}-${articleImpressions.map((x) => x.number).join("|")}`;
    if (articleImpressionKeyRef.current === key) return;
    articleImpressionKeyRef.current = key;
    posthog?.capture?.("SearchArticleImpression", {
      productLine: productLineForEvents,
      articleNumbers: articleImpressions.map((item) => item.number),
      articleTitles: articleImpressions.map((item) => item.title),
      searchType: "caseCreation"
    });
  }, [articleImpressions, componentLinkState, productLineForEvents, productName]);

  const buildCaseFormUrl = () => {
    const params = new URLSearchParams();
    if (productName) params.set("productName", productName);
    if (productLineCode) params.set("productLineCode", productLineCode);
    if (productSerial) params.set("productSerial", productSerial);
    return `${buildMarketPath(currentMarket, `/case/create/technical/${suboption}`)}?${params.toString()}`;
  };

  const handleContactSupport = () => {
    posthog?.capture?.("ContactCustomerSupportClick", {
      productLine: productLineForEvents,
      articleNumbers: articleImpressions.map((item) => item.number),
      articleTitles: articleImpressions.map((item) => item.title),
      caseType: "technical",
      caseSubtype: suboption
    });
    navigate(buildCaseFormUrl());
  };

  return (
    <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $unsetMargin={true}>
      <PageContainer>
        <Header>
          <BackButton size={Size.Small} onClick={() => window.history.back()}>
            {t("Back")}
          </BackButton>
          {!productLineLoading && (<Title>{productName || t("Product information")}</Title>)}
          {!productLineLoading && (<Subtitle textStyle={ComponentTextStyle.Regular}>
            {t("Choose an option below to access the support you need.")}
          </Subtitle>)}
        </Header>

        {productLineLoading && (
          <LoadingIndicatorContainer>
            <LoadingIndicator />
          </LoadingIndicatorContainer>
        )}

        {!productLineLoading && productLine && (
          <Components>
            <ComponentL textStyle={ComponentTextStyle.Bold}>{t("Popular information")}</ComponentL>
            {!isMediumScreen && (
              <DropdownFilter
                value={componentLinkState}
                list={componentItems.map((item) => ({
                  value: item.link,
                  displayLabel: item.name
                }))}
                onSelect={(value) => setComponentLinkState(value)}
              />
            )}
            <Row>
              {isMediumScreen && (
                <VerticalTabsContainer>
                  {componentItems.map((item, index) => (
                    <VerticalTab
                      text={item.name}
                      isActive={item.link === componentLinkState}
                      onClick={() => setComponentLinkState(item.link)}
                      key={`vertical-tab-${index}`}
                    />
                  ))}
                </VerticalTabsContainer>
              )}
              <Articles>
                {articlesLoading && (
                  <LoadingIndicatorContainer>
                    <LoadingIndicator />
                  </LoadingIndicatorContainer>
                )}
                {!articlesLoading &&
                  articlesDto?.articles.slice(0, 8).map((item, index) => (
                    <FaqLink
                      onClick={(e) => {
                        e.preventDefault();
                        posthog?.capture?.("SearchArticleClick", {
                          productLine: productLineForEvents,
                          articleNumber: item.articleNumber,
                          articleTitle: item.name,
                          searchType: "caseCreation"
                        });
                        navigate(`/articles/Knowledge/${item.link}`);
                      }}
                      href={`/articles/Knowledge/${item.link}`}
                      key={`link-${index}`}
                    >
                      {item.name}
                    </FaqLink>
                  ))}
              </Articles>
            </Row>
            <Footer>
              <FooterText>
                <FooterTitle>{t("Still need help?")}</FooterTitle>
                <FooterSubtitle>
                  {t("If you're still facing issues, our support team is just a click away. Reach out for personalized help!")}
                </FooterSubtitle>
              </FooterText>
              <ContactButton size={Size.Large} onClick={handleContactSupport}>
                {t("Contact customer support")}
              </ContactButton>
            </Footer>
            <UrgentAssistance pageName="caseAssetSelect" />
          </Components>
        )}

      </PageContainer>
    </PageWidthFull>
  );
};

export default CaseAssetInfoPage;
